/** Pasos del wizard de encuesta post-pago (clientes que vuelven / over_60). */
export type PostPaymentSurveyStep = 'P1' | 'P1a' | 'P1b' | 'P1c' | 'P2' | 'P2a' | 'P2b' | 'P3';

export interface SurveyOption {
    value: string;
    label: string;
}

export const SURVEY_OTHER_VALUE = 'other';

const SURVEY_OTHER_BLOCKED = new Set(['otro', 'otra', 'ninguna', 'ninguno', 'no sé', 'no se', 'n/a', 'na', 'no aplica', 'nada', 'no hay']);

export const SURVEY_P1_OPTIONS: SurveyOption[] = [
    { value: 'features', label: 'Funcionalidades' },
    { value: 'business_closed', label: 'El negocio quebró' },
    { value: 'did_not_know_how', label: 'No sabía usar bien Fina' },
    { value: 'lack_of_time', label: 'Falta de tiempo' },
    { value: 'too_expensive', label: 'Fina era muy caro' },
];

export const SURVEY_P1A_OPTIONS: SurveyOption[] = [
    { value: 'did_not_finish_setup', label: 'No terminé de configurar mi negocio en Fina' },
    { value: 'configured_not_daily', label: 'Terminé de configurarlo, pero no llegué a usarlo día a día' },
    { value: 'daily_no_reports', label: 'Lo usaba día a día, pero no llegué a revisar reportes o utilidad' },
    {
        value: 'business_blocked_time',
        label: 'Podía usar Fina, pero mi negocio no me dejó tiempo para nada en ese período (ej. temporada alta, personal, salud)',
    },
];

export const SURVEY_P1B_OPTIONS: SurveyOption[] = [
    { value: 'orders', label: 'Pedidos' },
    { value: 'inventory', label: 'Inventario' },
    { value: 'accounts', label: 'Cuentas' },
    { value: 'expenses', label: 'Gastos' },
    { value: 'financial_summary', label: 'Resumen Financiero' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'statistics', label: 'Estadísticas' },
];

export const SURVEY_P1C_OPTIONS: SurveyOption[] = [
    { value: 'fiscal_invoicing', label: 'Facturación fiscal' },
    { value: 'comandas', label: 'Comandas' },
    { value: 'tables', label: 'Mesas' },
    { value: 'bcv_rates', label: 'Tasas BCV automáticas' },
    { value: SURVEY_OTHER_VALUE, label: 'Otro' },
];

export const SURVEY_P2_OPTIONS: SurveyOption[] = [
    { value: 'new_feature', label: 'Descubrí que Fina tenía una funcionalidad nueva' },
    { value: 'more_time', label: 'Ahora tengo más tiempo' },
    { value: 'new_merchandise', label: 'Me llegó nueva mercancía' },
    { value: 'can_afford_now', label: 'Ahora sí puedo pagar Fina' },
    { value: 'someone_explained', label: 'Alguien me explicó cómo usar Fina' },
    { value: 'hired_someone', label: 'Contraté a alguien que iba a usar Fina por mí' },
];

export const SURVEY_P2A_OPTIONS: SurveyOption[] = [
    { value: 'tables', label: 'Mesas' },
    { value: 'bcv_rates', label: 'Tasas BCV automáticas' },
    { value: SURVEY_OTHER_VALUE, label: 'Otro' },
];

export const SURVEY_P2B_OPTIONS: SurveyOption[] = [
    { value: 'fina_team', label: 'El equipo de soporte de Fina' },
    { value: 'my_employee', label: 'Alguien de mi negocio/equipo' },
    { value: 'friend_or_acquaintance', label: 'Un colega o conocido que usa Fina' },
];

export const SURVEY_P3_OPTIONS: SurveyOption[] = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Email' },
    { value: 'phone_call', label: 'Me llamaron' },
    { value: 'social_ad', label: 'Vi un anuncio en redes' },
    { value: 'own_decision', label: 'No, decidí volver por mi cuenta' },
];

export const SURVEY_STEP_TITLES: Record<PostPaymentSurveyStep, string> = {
    P1: '¿Por qué te fuiste?',
    P1a: '¿En qué momento te faltó tiempo?',
    P1b: '¿En qué módulo tuviste más dificultad?',
    P1c: '¿Qué categoría de funcionalidad sentiste que le faltaba?',
    P2: '¿Por qué volviste?',
    P2a: '¿Cuál funcionalidad descubriste?',
    P2b: '¿Quién te explicó?',
    P3: '¿Recibiste un comunicado de Fina (WhatsApp, email o llamada) que te haya motivado a volver, antes de reactivar tu cuenta?',
};

/** Baraja una copia del array (Fisher–Yates). */
export function shuffleSurveyOptions<T>(items: T[]): T[] {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

/** Validación de texto libre "Otro" (P1c / P2a). */
export function isValidSurveyOtherText(text: string | null | undefined): boolean {
    const trimmed = (text ?? '').trim();
    if (trimmed.length < 3) {
        return false;
    }
    if (!/[0-9A-Za-zÀ-ÿ]/.test(trimmed)) {
        return false;
    }
    const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
    return !SURVEY_OTHER_BLOCKED.has(normalized);
}
